(ns manatee.annotation.app-db
  (:require [manatee.admin.task-config.app-db :as task-config-db]
            [manatee.domain.task :as task]
            [medley.core :refer [find-first indexed]]
            [utility-belt.pure :refer [contains-in?]]))

(def annotation-root [:annotation])
(def items-path (conj annotation-root :items))
(def current-item-path (conj annotation-root :current-item))
(def primary-item-path (conj annotation-root :primary-item))
(def task-configs-path [:task-configs])
(def loading?-path (conj annotation-root :loading?))
(def loading-text-visible-path (conj annotation-root :loading-text-visible))
(def current-segment-id-path (conj annotation-root :current-segment-id))
(def segment-id-to-select-path (conj annotation-root :segment-id-to-select))
(def segment-documents-path (conj annotation-root :segment-documents))
(def segments-path (conj annotation-root :segments))
(def intermediate-edit-path (conj annotation-root :intermediate-edit))
(def notes-path (conj annotation-root :notes))
(def valid-path (conj annotation-root :valid?))
(def validate-now-path (conj annotation-root :validate-now?))
(def reload-segments-path (conj annotation-root :reload-segments?))
(def time-markers-path (conj annotation-root :time-markers))
(def same-local-and-submitted-document?-path (conj annotation-root :same-local-and-submitted-document?))
(def autosaving?-path (conj annotation-root :autosaving?))
(def offer-clearing-autosaved-items-path (conj annotation-root :offer-clearing-autosaved-items))
(def autosaved-values-path (conj annotation-root :autosaved-values))
(def editor-view-path (conj annotation-root :editor-view))

(defn get-items
  [db]
  (get-in db items-path))

(defn get-item
  [db item-id]
  (find-first #(= (:id %) item-id) (get-items db)))

(defn items-type-filter
  [type items]
  (filter #(= type (:item-type %)) items))

(defn items-by-type
  [db type]
  (items-type-filter type (get-items db)))

(defn current-item
  [db]
  (get-in db current-item-path))

(defn set-current-item
  [db id]
  (assoc-in db current-item-path id))

(defn get-current-item-map
  [db]
  (get-item db (current-item db)))

(defn get-item-index
  [db item-id]
  (->> (get-items db)
       indexed
       (find-first #(= (:id (second %)) item-id))
       first))

(defn update-item
  "Relies on annotation items being stored as a vector to update items in place"
  [db item-id & args]
  (let [item-index (get-item-index db item-id)]
    (apply update-in db (conj items-path item-index) args)))

(defn set-items
  [db items]
  (assoc-in db items-path (vec items)))

(defn items-loaded?
  [db]
  (contains-in? db items-path))

(defn loading?
  [db]
  (boolean (get-in db loading?-path)))

(defn set-loading?
  [db loading?]
  (assoc-in db loading?-path loading?))

(defn unannotated-items
  [db edits]
  (remove #((set (keys edits)) (:id %)) (get-items db)))

(defn get-primary-item
  [db]
  (get-in db primary-item-path))

(defn set-primary-item
  [db id]
  (assoc-in db primary-item-path id))

(defn unset-annotation
  [db]
  (dissoc db :annotation))

(defn set-feedback-status
  [db item-id status]
  (update-item db item-id assoc-in [:feedback :status] status))

(defn suppress-part-screen?
  ([db]
   (suppress-part-screen? db (-> (get-items db)
                                 first
                                 :task-config-id)))
  ([db task-config-id]
   (task/modifier
    (task-config-db/get-task-config db task-config-id)
    :suppress-part-screen?)))

(defn part-like?
  [db type]
  (or (= type :part)
      (suppress-part-screen? db)))

(defn set-loading-text-visible
  [db visible?]
  (assoc-in db loading-text-visible-path visible?))

(defn loading-text-visible
  [db]
  (get-in db loading-text-visible-path))

(defn set-by-item-id
  [db path item-id value]
  (cond-> db item-id (assoc-in path {item-id value})))

(defn set-by-item-and-qa-edit-id
  "The `qa-edit-id` is optional and is used in QA to facilitate multiple edits on the screen.
  It will be nil in annotator's annotation."
  [db path item-id qa-edit-id value]
  (set-by-item-id db path item-id (assoc
                                   (get-in db (conj path item-id))
                                   qa-edit-id value)))

(defn current-segment-id
  [db item-id]
  (get-in db (conj current-segment-id-path item-id)))

(defn set-current-segment-id
  [db item-id segment-id]
  (set-by-item-id db current-segment-id-path item-id segment-id))

(defn segment-id-to-select
  [db item-id]
  (get-in db (conj segment-id-to-select-path item-id)))

(defn set-segment-id-to-select
  [db item-id segment-id]
  (set-by-item-id db segment-id-to-select-path item-id segment-id))

(defn segment-document
  ([db item-id] (segment-document db item-id (current-segment-id db item-id)))
  ([db item-id segment-id]
   (when segment-id
     (get-in db (conj segment-documents-path item-id segment-id)))))

(defn set-segment-document
  [db item-id document]
  (let [segment-id (current-segment-id db item-id)]
    (cond-> db (and item-id segment-id) (assoc-in (conj segment-documents-path item-id segment-id) document))))

(defn all-segment-documents
  [db item-id]
  (get-in db (conj segment-documents-path item-id)))

(defn set-all-segment-documents
  [db item-id documents]
  (set-by-item-id db segment-documents-path item-id documents))

(defn segments
  [db item-id]
  (get-in db (conj segments-path item-id)))

(defn set-segments
  [db item-id new-segments]
  (set-by-item-id db segments-path item-id new-segments))

(defn intermediate-edit
  [db item-id qa-edit-id]
  (get-in db (conj intermediate-edit-path item-id qa-edit-id) {}))

(defn set-intermediate-edit
  [db item-id qa-edit-id edit]
  (set-by-item-and-qa-edit-id db intermediate-edit-path item-id qa-edit-id edit))

(defn get-notes
  [db]
  (get-in db notes-path))

(defn set-notes
  [db notes]
  (assoc-in db notes-path notes))

(defn valid?
  [db item-id qa-edit-id]
  (get-in db (conj valid-path item-id qa-edit-id) false))

(defn set-valid
  [db item-id qa-edit-id valid?]
  (set-by-item-and-qa-edit-id db valid-path item-id qa-edit-id valid?))

(defn validate-now?
  [db item-id qa-edit-id]
  (get-in db (conj validate-now-path item-id qa-edit-id) false))

(defn set-validate-now
  [db item-id qa-edit-id validate-now?]
  (set-by-item-and-qa-edit-id db validate-now-path item-id qa-edit-id validate-now?))

(defn reload-segments?
  [db item-id]
  (get-in db (conj reload-segments-path item-id) false))

(defn set-reload-segments
  [db item-id reload-segments?]
  (set-by-item-id db reload-segments-path item-id reload-segments?))

(defn time-markers
  [db item-id]
  (get-in db (conj time-markers-path item-id)))

(defn set-time-markers
  [db item-id new-time-markers]
  (set-by-item-id db time-markers-path item-id new-time-markers))

(defn same-local-and-submitted-document?
  [db]
  (get-in db same-local-and-submitted-document?-path true))

(defn set-same-local-and-submitted-document?
  [db equal?]
  (assoc-in db same-local-and-submitted-document?-path equal?))

(defn autosaving?
  [db]
  (get-in db autosaving?-path false))

(defn set-autosaving?
  [db autosaving?]
  (assoc-in db autosaving?-path autosaving?))

(defn offer-clearing-autosaved-items
  [db]
  (get-in db offer-clearing-autosaved-items-path))

(defn set-offer-clearing-autosaved-items
  [db value]
  (assoc-in db offer-clearing-autosaved-items-path value))

(defn autosaved-values
  [db key]
  (get-in db (conj autosaved-values-path key)))

(defn set-autosaved-values
  [db values]
  (assoc-in db autosaved-values-path values))

(defn editor-view
  [db item-id]
  (get-in db (conj editor-view-path item-id)))

(defn set-editor-view
  [db item-id view]
  (set-by-item-id db editor-view-path item-id view))
